import React from "react";
import { Link } from "react-router-dom";

const FreedomOfPress = () => {
  const pressFreedomPrinciples = [
    {
      id: 1,
      title: "Informant Anonymity Protection",
      icon: "🛡️",
      description:
        "Members of the NAI guarantee their informants anonymity and protect them from possible sanctions.",
      details: [
        "Absolute protection of source identities",
        "Legal safeguarding against sanctions",
        "Secure communication channels",
        "Confidential handling of sensitive information",
      ],
      constitutionalBasis:
        "Article 19(1)(a) - Freedom of Speech and Expression",
    },
    {
      id: 2,
      title: "Constitutional Press Freedom",
      icon: "📜",
      description:
        "This protection of information is an essential part of the freedom of the press guaranteed in the Indian Constitution.",
      details: [
        "Fundamental right under Indian Constitution",
        "Essential for democratic functioning",
        "Basis for investigative journalism",
        "Protection against censorship",
      ],
      constitutionalBasis: "Article 19 - Fundamental Rights",
    },
    {
      id: 3,
      title: "Legal & Political Support",
      icon: "⚖️",
      description:
        "If citizen reporters and professional journalist members of the NAI are impaired in doing their work, they can report this to the NAI.",
      details: [
        "Immediate reporting mechanism",
        "Political advocacy support",
        "Legal intervention when necessary",
        "Protection against work impediments",
      ],
      constitutionalBasis: "Press Council Act, 1978",
    },
  ];

  const supportMechanisms = [
    {
      category: "Legal Protection",
      items: [
        "Legal representation for harassed journalists",
        "Challenges to unconstitutional restrictions",
        "Support in defamation cases",
        "Assistance with Right to Information appeals",
      ],
      icon: "⚖️",
    },
    {
      category: "Political Advocacy",
      items: [
        "Lobbying for press freedom legislation",
        "Engagement with government bodies",
        "International press freedom advocacy",
        "Public awareness campaigns",
      ],
      icon: "🗳️",
    },
    {
      category: "Member Support",
      items: [
        "24/7 emergency helpline",
        "Legal consultation services",
        "Safety training programs",
        "Digital security assistance",
      ],
      icon: "🤝",
    },
  ];

  const constitutionalProvisions = [
    {
      article: "Article 19(1)(a)",
      title: "Freedom of Speech and Expression",
      description:
        "Guarantees all citizens the right to freedom of speech and expression, forming the foundation for press freedom in India.",
      implications: [
        "Right to publish and circulate information",
        "Freedom to criticize government policies",
        "Protection against prior restraint",
        "Right to receive information",
      ],
    },
    {
      article: "Article 19(2)",
      title: "Reasonable Restrictions",
      description:
        "Allows for reasonable restrictions in interests of sovereignty, security, public order, decency, morality, etc.",
      implications: [
        "Balancing press freedom with social interests",
        "Judicial review of restrictions",
        "Proportionality principle application",
        "Protection against arbitrary censorship",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Freedom of the Press
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              Protecting Journalistic Freedom and Informant Rights as Guaranteed
              by the Indian Constitution
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Constitutional Protection of Press Freedom
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 text-center">
            <p className="text-lg leading-relaxed mb-6">
              The Newsmedia Association of India stands firmly for the
              protection of press freedom as a fundamental pillar of Indian
              democracy. Our commitment extends to safeguarding both journalists
              and their sources through legal, political, and constitutional
              means.
            </p>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Core Principles of Press Freedom
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fundamental rights and protections for NAI members and their
              informants
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {pressFreedomPrinciples.map((principle) => (
              <div
                key={principle.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 group"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-4xl">{principle.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-white text-center">
                    {principle.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    {principle.description}
                  </p>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-3">
                        Key Protections:
                      </h4>
                      <ul className="space-y-2">
                        {principle.details.map((detail, index) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-gray-600 text-sm">
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">
                        Constitutional Basis:
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {principle.constitutionalBasis}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Constitutional Framework */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Constitutional Framework
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Legal foundations of press freedom in the Indian Constitution
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {constitutionalProvisions.map((provision, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200"
              >
                <div className="text-center mb-6">
                  <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold">
                    {provision.article}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mt-4">
                    {provision.title}
                  </h3>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6 text-center">
                  {provision.description}
                </p>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">
                    Key Implications:
                  </h4>
                  <ul className="space-y-3">
                    {provision.implications.map((implication, impIndex) => (
                      <li key={impIndex} className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                          <span className="text-blue-600 text-sm font-bold">
                            {impIndex + 1}
                          </span>
                        </div>
                        <span className="text-gray-700 leading-relaxed">
                          {implication}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Mechanisms */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              NAI Support Mechanisms
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive support for journalists facing impediments in their
              work
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportMechanisms.map((mechanism, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200 text-center"
              >
                <div className="text-3xl mb-4">{mechanism.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  {mechanism.category}
                </h3>

                <ul className="space-y-3 text-left">
                  {mechanism.items.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex items-start text-gray-700"
                    >
                      <span className="text-green-500 mr-2 mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reporting Procedure */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">
                Reporting Work Impediments
              </h2>
              <p className="text-blue-100 text-lg">
                Step-by-step procedure for NAI members to report obstructions in
                journalistic work
              </p>
              <div className="w-24 h-1 bg-white mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="text-2xl mr-3">📞</span>
                  Immediate Steps
                </h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">1.</span>
                    Contact NAI helpline immediately
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">2.</span>
                    Document all evidence of obstruction
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">3.</span>
                    Preserve digital and physical records
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="text-2xl mr-3">⚖️</span>
                  NAI Intervention
                </h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">•</span>
                    Legal representation provision
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">•</span>
                    Political advocacy initiation
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">•</span>
                    Media awareness campaign
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Emergency Press Freedom Helpline
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Immediate assistance for journalists facing threats, harassment,
              or work impediments
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-2xl text-red-600 mb-2">📞</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  24/7 Helpline
                </h3>
                <p className="text-2xl font-bold text-red-600">72000 24451</p>
                <p className="text-gray-600 text-sm mt-2">
                  Immediate legal assistance
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-2xl text-green-600 mb-2">💬</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  WhatsApp Support
                </h3>
                <p className="text-2xl font-bold text-green-600">72000 24452</p>
                <p className="text-gray-600 text-sm mt-2">
                  Secure digital communication
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Defend Press Freedom With Us
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            Join NAI in protecting the fundamental right to free press and
            ensuring journalists can work without fear or impediment
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Report an Incident
            </button>
            <Link to ="/apply/citizen-reporter" className="border-2 border-white hover:bg-white hover:text-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
             Become a Citizen Reporter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreedomOfPress;
